import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePermissions } from "../../../PermissionsProvider";
import { DatasetService } from "../../../services/DatasetService";
import { CompositionService } from "../../../services/CompositionService";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import DatasetsTable from "../../shared/DatasetsTable";
import CompositionsTable from "../../shared/CompositionsTable";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import { PlusIcon, LayersIcon, DatabaseIcon } from "lucide-react";
import { cn } from "../../../lib/utils";

interface TabItem {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const Compositions: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { apiKey } = usePermissions();

  const [loading, setLoading] = useState<boolean>(false);
  const [datasets, setDatasets] = useState<FinetuneDataset[]>([]);
  const [compositions, setCompositions] = useState<any[]>([]);
  const [totalCompositions, setTotalCompositions] = useState<number>(0);
  const [totalDatasets, setTotalDatasets] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [activeTab, setActiveTab] = useState<string>(() => {
    const tab = searchParams.get("tab");
    return tab === "datasets" ? "datasets" : "compositions";
  });

  const pageSize = 10;

  useEffect(() => {
    if (activeTab === "compositions") {
      fetchCompositions();
    } else if (activeTab === "datasets") {
      fetchDatasets();
    }
  }, [currentPage, apiKey, activeTab]);

  const fetchCompositions = () => {
    const offset = (currentPage - 1) * pageSize;
    CompositionService.getCompositions(
      offset,
      pageSize,
      setLoading,
      apiKey
    ).subscribe(
      (response: any) => {
        setCompositions(response.items || []);
        setTotalCompositions(response.total || 0);
      },
      (error: any) => {
        console.error("Error fetching compositions:", error);
        setCompositions([]);
        setTotalCompositions(0);
      }
    );
  };

  const fetchDatasets = () => {
    const offset = (currentPage - 1) * pageSize;
    DatasetService.getDatasets(offset, pageSize, setLoading, apiKey).subscribe(
      (response: any) => {
        setDatasets(response.items || []);
        setTotalDatasets(response.total || 0);
      },
      (error: any) => {
        console.error("Error fetching datasets:", error);
        setDatasets([]);
        setTotalDatasets(0);
      }
    );
  };

  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setCurrentPage(1);

    // Update URL parameters
    if (key === "datasets") {
      setSearchParams({ tab: "datasets" });
    } else {
      setSearchParams({});
    }
  };

  const handleDeleteDataset = (dataset: FinetuneDataset) => {
    DatasetService.deleteDataset(dataset.id, null, apiKey).subscribe(
      () => {
        fetchDatasets(); // Refresh the data
      },
      (error: any) => {
        console.error("Error deleting dataset:", error);
      }
    );
  };

  const handleDeleteComposition = (composition: any) => {
    CompositionService.deleteComposition(
      composition.id.toString(),
      null,
      apiKey
    ).subscribe(
      () => {
        fetchCompositions(); // Refresh the data
      },
      (error: any) => {
        console.error("Error deleting composition:", error);
      }
    );
  };

  const getActionButton = () => {
    if (activeTab === "compositions") {
      return (
        <Button
          onClick={() => navigate("/forge/compositions/new")}
          className="gap-2">
          <PlusIcon className="w-4 h-4" />
          New Composition
        </Button>
      );
    }

    return (
      <Button onClick={() => navigate("/forge/datasets/new")} className="gap-2">
        <PlusIcon className="w-4 h-4" />
        New Dataset
      </Button>
    );
  };

  // Tab configuration
  const tabs: TabItem[] = [
    {
      key: "compositions",
      label: "Compositions",
      icon: <LayersIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">
                Compositions
              </h3>
              <p className="text-sm text-muted-foreground">
                Manage and organize your model compositions
              </p>
            </div>
            {getActionButton()}
          </div>

          <CompositionsTable
            compositions={compositions}
            loading={loading}
            currentPage={currentPage}
            totalPages={Math.ceil(totalCompositions / pageSize)}
            totalItems={totalCompositions}
            itemsPerPage={pageSize}
            onPageChange={setCurrentPage}
            onDeleteComposition={handleDeleteComposition}
            emptyTitle="No compositions found"
            emptyDescription="Create your first composition to get started with model fine-tuning."
          />
        </div>
      ),
    },
    {
      key: "datasets",
      label: "Datasets",
      icon: <DatabaseIcon className="w-4 h-4" />,
      content: (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-gc-med text-foreground">Datasets</h3>
              <p className="text-sm text-muted-foreground">
                Manage and organize your training datasets
              </p>
            </div>
            {getActionButton()}
          </div>

          <DatasetsTable
            datasets={datasets}
            loading={loading}
            currentPage={currentPage}
            totalPages={Math.ceil(totalDatasets / pageSize)}
            totalItems={totalDatasets}
            itemsPerPage={pageSize}
            onPageChange={setCurrentPage}
            onDeleteDataset={handleDeleteDataset}
            emptyTitle="No datasets found"
            emptyDescription="Create your first dataset to get started with model fine-tuning."
          />
        </div>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button
                    onClick={() => navigate("/forge")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg">
                    Forge
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">
                    Compositions
                  </span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="space-y-1">
              <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                Compositions
              </h1>
              <p className="text-lg text-muted-foreground font-gc-reg">
                Manage compositions and datasets for model fine-tuning
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <div className="border-b border-border">
              <nav className="flex space-x-8 px-8 py-4">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 text-sm font-gc-med rounded-md transition-colors",
                      activeTab === tab.key
                        ? "bg-maitai-green/10 text-maitai-green"
                        : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                    )}>
                    {tab.icon}
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {tabs.find((tab) => tab.key === activeTab)?.content}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Compositions;
